import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { paymentApi, memberApi } from '../../services/api';
import { toast } from 'react-toastify';
import { formatDate, formatDateRange } from '../../utils/dateUtils';
import TableFilter from '../../components/TableFilter';
import SortableTable from '../../components/SortableTable';

const PaymentList = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [members, setMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all payments
        const paymentsResponse = await paymentApi.getAll(false); // Get payments from all years
        
        // Sort payments by date (newest first)
        const sortedPayments = [...paymentsResponse.data].sort((a, b) => {
          return new Date(b.paid_on) - new Date(a.paid_on);
        });
        
        setPayments(sortedPayments);
        setFilteredPayments(sortedPayments);
        
        // Fetch all members to get their usernames
        const membersResponse = await memberApi.getAll();
        const membersMap = {};
        membersResponse.data.forEach(member => {
          membersMap[member.id] = member.username || 'Unknown';
        });
        setMembers(membersMap);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load payments');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this payment?')) {
      try {
        await paymentApi.delete(id);
        const updatedPayments = payments.filter(payment => payment.id !== id);
        setPayments(updatedPayments);
        setFilteredPayments(updatedPayments);
      } catch (err) {
        setError('Failed to delete payment');
        console.error(err);
      }
    }
  };

  const handleFilter = (filterText) => {
    if (!filterText) {
      setFilteredPayments(payments);
      return;
    }
    
    const lowercasedFilter = filterText.toLowerCase();
    const filtered = payments.filter(payment => {
      const memberUsername = members[payment.member_id] || '';
      return (
        memberUsername.toLowerCase().includes(lowercasedFilter) ||
        payment.amount.toString().includes(lowercasedFilter) ||
        formatDate(payment.paid_on).includes(lowercasedFilter)
      );
    });
    
    setFilteredPayments(filtered);
  };
  
  const handleApprovePayment = async (paymentId) => {
    try {
      setLoading(true);
      await paymentApi.approve(paymentId);
      
      // Refresh the payments list
      const paymentsResponse = await paymentApi.getAll(false);
      const sortedPayments = [...paymentsResponse.data].sort((a, b) => {
        return new Date(b.paid_on) - new Date(a.paid_on);
      });
      
      setPayments(sortedPayments);
      setFilteredPayments(sortedPayments);
      setLoading(false);
      
      toast.success('Payment approved successfully');
    } catch (err) {
      setError('Failed to approve payment');
      setLoading(false);
      console.error(err);
      toast.error('Failed to approve payment');
    }
  };

  const columns = useMemo(() => [
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (payment) => (
        <span className={`badge ${payment.status === 'expected' ? 'bg-warning text-dark' : 'bg-success'}`}>
          {payment.status === 'expected' ? 'Expected' : 'Paid'}
        </span>
      )
    },
    {
      key: 'member_id',
      label: 'Username',
      sortable: true,
      render: (payment) => (
        <span className="fw-bold">
          {members[payment.member_id] || 'Unknown Member'}
        </span>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: true,
      render: (payment) => `${payment.amount.toFixed(2)} €`
    },
    {
      key: 'paid_on',
      label: 'Date',
      sortable: true,
      render: (payment) => formatDate(payment.paid_on)
    },
    {
      key: 'period',
      label: 'Period',
      sortable: false,
      render: (payment) => {
        // Check if valid_from and valid_until are present and not null
        if (payment.valid_from && payment.valid_until) {
          return formatDateRange(payment.valid_from, payment.valid_until);
        } else {
          return 'N/A';
        }
      }
    },
    {
      key: 'valid_from',
      label: 'Valid From',
      sortable: true,
      render: (payment) => formatDate(payment.valid_from)
    },
    {
      key: 'valid_until',
      label: 'Valid Until',
      sortable: true,
      render: (payment) => formatDate(payment.valid_until)
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      className: 'text-end',
      headerClassName: 'text-end',
      style: { width: '1%', whiteSpace: 'nowrap' },
      render: (payment) => (
        <div className="btn-group">
          {payment.status === 'expected' ? (
            <button
              onClick={() => handleApprovePayment(payment.id)}
              className="btn btn-sm btn-success"
              title="Approve Payment"
            >
              <i className="fas fa-check me-1"></i> Approve
            </button>
          ) : null}
          <Link to={`/members/${payment.member_id}`} className="btn btn-sm btn-outline-primary" title="View Member">
            <i className="fas fa-eye"></i>
          </Link>
          <Link to={`/payments/${payment.id}/edit`} className="btn btn-sm btn-outline-secondary" title="Edit">
            <i className="fas fa-edit"></i>
          </Link>
          <button 
            onClick={() => handleDelete(payment.id)} 
            className="btn btn-sm btn-outline-danger"
            title="Delete"
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </div>
      )
    }
  ], [members]);

  if (loading) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="display-5 mb-0">Payments</h1>
            <Link to="/payments/new" className="btn btn-success">
              <i className="fas fa-plus-circle me-2"></i>
              Record New Payment
            </Link>
          </div>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="row">
          <div className="col">
            <div className="alert alert-info d-flex align-items-center">
              <i className="fas fa-info-circle me-3 fs-4"></i>
              <div>
                No payments found. Record your first payment to get started.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fas fa-credit-card me-2"></i>
                  Payment History
                </h5>
                <span className="badge bg-primary rounded-pill px-3 py-2 fs-6">
                  {filteredPayments.length} payments
                </span>
              </div>
              <div className="card-body p-0">
                <div className="p-3 border-bottom">
                  <TableFilter 
                    onFilter={handleFilter} 
                    placeholder="Search payments..."
                  />
                </div>
                <div className="table-responsive">
                  <SortableTable 
                    columns={columns} 
                    data={filteredPayments} 
                    keyField="id"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentList;
